// src/components/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { Users, Radio, DollarSign, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCreators: 0,
    totalPodcasts: 0,
    pendingPodcasts: 0,
    monthlyRevenue: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Get user stats
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const totalUsers = users.length;
      const totalCreators = users.filter(user => user.isCreator).length;

      // Get podcast stats
      const podcastsSnapshot = await getDocs(collection(db, 'podcasts'));
      const podcasts = podcastsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const totalPodcasts = podcasts.length;
      const pendingPodcasts = podcasts.filter(podcast => podcast.status === 'pending').length;

      // Calculate revenue (mock data for now)
      const monthlyRevenue = podcasts.reduce((sum, podcast) => 
        sum + (podcast.monetization?.monthlyRevenue || 0), 0);
      const totalRevenue = podcasts.reduce((sum, podcast) => 
        sum + (podcast.monetization?.totalRevenue || 0), 0);

      // Recent activity
      const recentPodcasts = podcasts
        .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
        .slice(0, 5);

      setStats({
        totalUsers,
        totalCreators,
        totalPodcasts,
        pendingPodcasts,
        monthlyRevenue,
        totalRevenue
      });

      setRecentActivity(recentPodcasts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, color = 'blue' }) => (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {change && (
            <p className={`text-sm mt-1 ${change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
              {change} fra sidste måned
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-600/20`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard Oversigt</h1>
        <p className="text-slate-400 mt-1">Velkommen til Faircast administratorpanel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Samlede Brugere"
          value={stats.totalUsers.toLocaleString('da-DK')}
          icon={Users}
          change="+12% fra sidste måned"
          color="blue"
        />
        <StatCard
          title="Skabere"
          value={stats.totalCreators.toLocaleString('da-DK')}
          icon={Radio}
          change="+8% fra sidste måned"
          color="emerald"
        />
        <StatCard
          title="Samlede Podcasts"
          value={stats.totalPodcasts.toLocaleString('da-DK')}
          icon={Radio}
          change="+15% fra sidste måned"
          color="purple"
        />
        <StatCard
          title="Afventende Godkendelse"
          value={stats.pendingPodcasts.toLocaleString('da-DK')}
          icon={AlertCircle}
          color="yellow"
        />
        <StatCard
          title="Månedlig Omsætning"
          value={`${stats.monthlyRevenue.toLocaleString('da-DK')} kr`}
          icon={DollarSign}
          change="+23% fra sidste måned"
          color="green"
        />
        <StatCard
          title="Samlet Omsætning"
          value={`${stats.totalRevenue.toLocaleString('da-DK')} kr`}
          icon={TrendingUp}
          color="blue"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Seneste Podcasts</h3>
          <div className="space-y-3">
            {recentActivity.map((podcast) => (
              <div key={podcast.id} className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                <div>
                  <p className="text-white font-medium">{podcast.title}</p>
                  <p className="text-slate-400 text-sm">af {podcast.creator}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    podcast.category === 'Imported' 
                      ? 'bg-yellow-600/20 text-yellow-400' 
                      : 'bg-green-600/20 text-green-400'
                  }`}>
                    {podcast.category}
                  </span>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Hurtige Handlinger</h3>
          <div className="space-y-3">
            <button className="w-full text-left p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
              <p className="text-white font-medium">Gennemse Afventende Indhold</p>
              <p className="text-blue-200 text-sm">{stats.pendingPodcasts} podcast(s) afventer godkendelse</p>
            </button>
            <button className="w-full text-left p-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors">
              <p className="text-white font-medium">Behandl Udbetalinger</p>
              <p className="text-emerald-200 text-sm">Månedlige skaber-udbetalinger</p>
            </button>
            <button className="w-full text-left p-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors">
              <p className="text-white font-medium">Bruger Support</p>
              <p className="text-purple-200 text-sm">Se åbne support-sager</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;